import { atom, transact } from '@tldraw/state'
import { noop } from '@tldraw/utils'
import { publishDates, version } from '../../version'
import { getDefaultCdnBaseUrl } from '../utils/assets'
import { importPublicKey, str2ab } from '../utils/licensing'

const GRACE_PERIOD_DAYS = 30

export const FLAGS = {
	// -- MUTUALLY EXCLUSIVE FLAGS
	// Annual means the license expires after a time period, usually 1 year.
	ANNUAL_LICENSE: 1,
	// Perpetual means the license never expires up to the max supported version.
	PERPETUAL_LICENSE: 1 << 1,

	// -- ADDITIVE FLAGS --
	// Internal means the license is for internal use only.
	INTERNAL_LICENSE: 1 << 2,
	// Watermark means the product is watermarked.
	WITH_WATERMARK: 1 << 3,
	// Evaluation means the license is for evaluation purposes only.
	EVALUATION_LICENSE: 1 << 4,
	// Native means the license is for native apps which switches
	// on special-case logic.
	NATIVE_LICENSE: 1 << 5,

	// -- FEATURE FLAGS --
	// Collaboration is the umbrella flag for collaboration features; it grants all sub-features.
	FEAT_COLLABORATION: 1 << 6,
	// Commenting is the first sub-feature of collaboration.
	FEAT_COMMENTING: 1 << 7,
}
const HIGHEST_FLAG = Math.max(...Object.values(FLAGS))

export const PROPERTIES = {
	ID: 0,
	HOSTS: 1,
	FLAGS: 2,
	EXPIRY_DATE: 3,
}
const NUMBER_OF_KNOWN_PROPERTIES = Object.keys(PROPERTIES).length

const LICENSE_EMAIL = 'sales@tldraw.com'

/**
 * tldraw's own public domains. A page served from one of these hosts — or any subdomain of one — is
 * licensed without a key, because we own them: minting and rotating keys for our own properties was
 * a recurring source of expired and wrong-domain deployments.
 *
 * This list ships in the bundle, so it holds only domains we already serve from publicly. tldraw's
 * private domains are deliberately absent and keep using a real license key.
 */
const CANONICAL_TLDRAW_HOSTS = [
	'tldraw.com',
	'tldraw.dev',
	'tldraw.club',
	'tldraw.workers.dev',
	'tldraw.xyz',
]

/** Marks a license as the keyless canonical-host grant, which is never tracked. */
const CANONICAL_HOST_LICENSE_ID = 'tldraw-canonical-host'

/** The canonical-host grant has no key behind it, and so no expiry date to fall due. */
const CANONICAL_HOST_NEVER_EXPIRES = new Date(8640000000000000)

const WATERMARK_TRACK_SRC = `${getDefaultCdnBaseUrl()}/watermarks/watermark-track.svg`

/** @internal */
export interface LicenseInfo {
	id: string
	hosts: string[]
	flags: number
	expiryDate: string
}

/**
 * Names of the licensable product features gated by the license. `collaboration` is an umbrella
 * that also grants all of its sub-features (currently just `commenting`).
 *
 * @internal
 */
export type LicenseFeatureName = 'collaboration' | 'commenting'

const NO_FEATURES: Readonly<Record<LicenseFeatureName, boolean>> = {
	collaboration: false,
	commenting: false,
}
const ALL_FEATURES: Readonly<Record<LicenseFeatureName, boolean>> = {
	collaboration: true,
	commenting: true,
}

/** @internal */
export type LicenseState =
	| 'pending' // License validation is in progress
	| 'licensed' // License is valid and active (no restrictions)
	| 'licensed-with-watermark' // License is valid but shows watermark (evaluation licenses, WITH_WATERMARK licenses)
	| 'unlicensed' // No valid license found or license is invalid (development)
	| 'unlicensed-production' // No valid license in production deployment (missing, invalid, or wrong domain)
	| 'expired' // License has been expired (30 days past expiration for regular licenses, immediately for evaluation licenses)
/** @internal */
export type InvalidLicenseReason =
	| 'invalid-license-key'
	| 'no-key-provided'
	| 'has-key-development-mode'

/** @internal */
export type LicenseFromKeyResult = InvalidLicenseKeyResult | ValidLicenseKeyResult

/** @internal */
export interface InvalidLicenseKeyResult {
	isLicenseParseable: false
	reason: InvalidLicenseReason
}

/** @internal */
export interface ValidLicenseKeyResult {
	isLicenseParseable: true
	license: LicenseInfo
	isDevelopment: boolean
	isDomainValid: boolean
	expiryDate: Date
	isAnnualLicense: boolean
	isAnnualLicenseExpired: boolean
	isPerpetualLicense: boolean
	isPerpetualLicenseExpired: boolean
	isInternalLicense: boolean
	isNativeLicense: boolean
	isLicensedWithWatermark: boolean
	isEvaluationLicense: boolean
	isEvaluationLicenseExpired: boolean
	isCollaborationEnabled: boolean
	isCommentingEnabled: boolean
	daysSinceExpiry: number
}

/** @internal */
export type TrackType = 'unlicensed' | 'with_watermark' | 'evaluation' | null

/** @internal */
export class LicenseManager {
	private publicKey =
		'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEHJh0uUfxHtCGyerXmmatE368Hd9rI6LH9oPDQihnaCryRFWEVeOvf9U/SPbyxX74LFyJs5tYeAHq5Nc0Ax25LQ'
	public isDevelopment: boolean
	public isTest: boolean
	public isCryptoAvailable: boolean
	state = atom<LicenseState>('license state', 'pending')
	featureFlags = atom<Record<LicenseFeatureName, boolean>>('license feature flags', {
		...NO_FEATURES,
	})
	public verbose = true
	private isDisposed = false

	constructor(licenseKey: string | undefined, testPublicKey?: string) {
		this.isTest = process.env.NODE_ENV === 'test'
		this.isDevelopment = this.getIsDevelopment()
		this.publicKey = testPublicKey || this.publicKey
		this.isCryptoAvailable = !!crypto.subtle

		// In development every feature is enabled (see `getEnabledFeatures`), and that doesn't depend
		// on the async validation result. Reflect it eagerly so features aren't reported as disabled
		// during the validation window — or left disabled if validation rejects (the `.catch` below
		// never sets `featureFlags`). In production the fail-closed default stands until validation.
		if (this.isDevelopment) {
			this.featureFlags.set({ ...ALL_FEATURES })
		}

		this.getLicenseFromKey(licenseKey)
			.then((result) => {
				// replaced by a manager for a newer key: this one's messages, tracking ping and state
				// would describe a key the app no longer uses
				if (this.isDisposed) return
				const licenseState = getLicenseState(
					result,
					(messages: string[]) => this.outputMessages(messages),
					this.isDevelopment
				)

				this.maybeTrack(result, licenseState)

				// Update both atoms atomically so dependents never observe the license state and the
				// feature flags out of sync mid-update.
				transact(() => {
					this.state.set(licenseState)
					this.featureFlags.set(getEnabledFeatures(result, licenseState, this.isDevelopment))
				})
			})
			.catch((error) => {
				if (this.isDisposed) return
				console.error('License validation failed:', error)
				this.state.set('unlicensed')
			})
	}

	/** Ignore the pending validation: nothing is logged, tracked or set once the manager is replaced. */
	dispose() {
		this.isDisposed = true
	}

	/**
	 * Returns whether a given licensable feature is enabled. Reactive: reading this inside a signal
	 * recomputes when license validation resolves.
	 */
	isFeatureEnabled(feature: LicenseFeatureName): boolean {
		return this.featureFlags.get()[feature]
	}

	private getIsDevelopment() {
		const { protocol, hostname } = this.getOriginLocation()

		// Tauri uses `tauri://localhost` on macOS and Linux and `http://tauri.localhost` on Windows.
		if (hostname.endsWith('.localhost')) {
			return process.env.NODE_ENV !== 'production'
		}

		return (
			protocol === 'http:' ||
			(protocol === 'https:' && this.isLoopbackHost(hostname)) ||
			process.env.NODE_ENV !== 'production'
		)
	}

	private getOriginLocation() {
		// A document loaded from `blob:https://example.com/<uuid>` has protocol `blob:` and an empty
		// hostname, but its origin is the page that created the blob. Opaque origins ('null': custom
		// schemes, file:, about:) keep using the location.
		const { origin, protocol, hostname } = window.location
		const source = origin && origin !== 'null' ? new URL(origin) : { protocol, hostname }
		return { protocol: source.protocol, hostname: source.hostname.toLowerCase() }
	}

	private isLoopbackHost(hostname: string) {
		// localhost, IPv4 loopback (127.0.0.0/8) and IPv6 loopback (::1) are all local development hosts
		const host = hostname.toLowerCase().replace(/^\[|\]$/g, '')
		return host === 'localhost' || host === '::1' || /^127(?:\.\d{1,3}){3}$/.test(host)
	}

	/** Whether the page is served from one of tldraw's own domains, or a subdomain of one. */
	private isCanonicalTldrawHost() {
		const hostname = window.location.hostname.toLowerCase()
		return CANONICAL_TLDRAW_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`))
	}

	/**
	 * The license tldraw's own domains get without a key. It keeps the watermark — our properties
	 * advertise the SDK the same way any other watermarked deployment does — and grants the
	 * collaboration features our apps are built on.
	 *
	 * Every expiry-derived field is a literal rather than something computed from a date, because
	 * there is no key here to expire: the grant lasts as long as the host is ours.
	 */
	private getCanonicalHostLicense(): ValidLicenseKeyResult {
		return {
			isLicenseParseable: true,
			license: {
				id: CANONICAL_HOST_LICENSE_ID,
				hosts: [window.location.hostname.toLowerCase()],
				flags: FLAGS.WITH_WATERMARK | FLAGS.FEAT_COLLABORATION | FLAGS.FEAT_COMMENTING,
				expiryDate: CANONICAL_HOST_NEVER_EXPIRES.toISOString(),
			},
			isDevelopment: this.isDevelopment,
			isDomainValid: true,
			expiryDate: CANONICAL_HOST_NEVER_EXPIRES,
			isAnnualLicense: false,
			isAnnualLicenseExpired: false,
			isPerpetualLicense: false,
			isPerpetualLicenseExpired: false,
			isInternalLicense: false,
			isNativeLicense: false,
			isLicensedWithWatermark: true,
			isEvaluationLicense: false,
			isEvaluationLicenseExpired: false,
			isCollaborationEnabled: true,
			isCommentingEnabled: true,
			daysSinceExpiry: 0,
		}
	}

	private getTrackType(result: LicenseFromKeyResult, licenseState: LicenseState): TrackType {
		// Our own domains are licensed without a key, and the watermark ping exists to tell us about
		// deployments we don't already know about. Reporting our own apps back to ourselves would only
		// add noise to the numbers we read, so the canonical-host grant is never tracked.
		if ('license' in result && result.license.id === CANONICAL_HOST_LICENSE_ID) {
			return null
		}

		// Track watermark for unlicensed production deployments
		if (licenseState === 'unlicensed-production') {
			return 'unlicensed'
		}

		if (this.isDevelopment) {
			return null
		}

		if (!result.isLicenseParseable) {
			return null
		}

		// Track evaluation licenses (for analytics, even though no watermark is shown)
		if (result.isEvaluationLicense) {
			return 'evaluation'
		}

		// Track licenses that show watermarks
		if (licenseState === 'licensed-with-watermark') {
			return 'with_watermark'
		}

		return null
	}

	private maybeTrack(result: LicenseFromKeyResult, licenseState: LicenseState): void {
		const trackType = this.getTrackType(result, licenseState)
		if (!trackType) {
			return
		}

		const url = new URL(WATERMARK_TRACK_SRC)
		url.searchParams.set('version', version)
		url.searchParams.set('license_type', trackType)
		if ('license' in result) {
			url.searchParams.set('license_id', result.license.id)
			const sku = this.isFlagEnabled(result.license.flags, FLAGS.EVALUATION_LICENSE)
				? 'evaluation'
				: this.isFlagEnabled(result.license.flags, FLAGS.ANNUAL_LICENSE)
					? 'annual'
					: this.isFlagEnabled(result.license.flags, FLAGS.PERPETUAL_LICENSE)
						? 'perpetual'
						: 'unknown'
			url.searchParams.set('sku', sku)
		}
		url.searchParams.set('url', window.location.href)
		if (process.env.NODE_ENV) {
			url.searchParams.set('environment', process.env.NODE_ENV)
		}

		// best-effort: a blocked or offline request must not surface as an unhandled rejection
		// eslint-disable-next-line no-restricted-globals
		fetch(url.toString()).catch(noop)
	}

	private async extractLicenseKey(licenseKey: string): Promise<LicenseInfo> {
		const [data, signature] = licenseKey.split('.')
		const [prefix, encodedData] = data.split('/')

		if (!prefix.startsWith('tldraw-')) {
			throw new Error(`Unsupported prefix '${prefix}'`)
		}

		const publicCryptoKey = await importPublicKey(this.publicKey)

		let isVerified
		try {
			isVerified = await crypto.subtle.verify(
				{
					name: 'ECDSA',
					hash: { name: 'SHA-256' },
				},
				publicCryptoKey,
				new Uint8Array(str2ab(atob(signature))),
				new Uint8Array(str2ab(atob(encodedData)))
			)
		} catch (e) {
			console.error(e)
			throw new Error('Could not perform signature validation')
		}

		if (!isVerified) {
			throw new Error('Invalid signature')
		}

		let decodedData: any
		try {
			decodedData = JSON.parse(atob(encodedData))
		} catch {
			throw new Error('Could not parse object')
		}
		if (decodedData.length > NUMBER_OF_KNOWN_PROPERTIES) {
			this.outputMessages([
				'License key contains some unknown properties.',
				'You may want to update tldraw packages to a newer version to get access to new functionality.',
			])
		}

		return {
			id: decodedData[PROPERTIES.ID],
			hosts: decodedData[PROPERTIES.HOSTS],
			flags: decodedData[PROPERTIES.FLAGS],
			expiryDate: decodedData[PROPERTIES.EXPIRY_DATE],
		}
	}

	async getLicenseFromKey(licenseKey?: string): Promise<LicenseFromKeyResult> {
		// A key that is provided still wins, so an app on one of our hosts can opt into a different
		// license — a native or feature-specific key — just by passing it.
		if (!licenseKey && this.isCanonicalTldrawHost()) {
			return this.getCanonicalHostLicense()
		}

		if (!licenseKey) {
			if (!this.isDevelopment) {
				this.outputNoLicenseKeyProvided()
			}

			return { isLicenseParseable: false, reason: 'no-key-provided' }
		}

		if (this.isDevelopment && !this.isCryptoAvailable) {
			if (this.verbose) {
				// eslint-disable-next-line no-console
				console.log(
					'tldraw: you seem to be in a development environment that does not support crypto. License not verified.'
				)
				// eslint-disable-next-line no-console
				console.log('You should check that this works in production separately.')
			}
			// We can't parse the license if we are in development mode since crypto
			// is not available on http
			return { isLicenseParseable: false, reason: 'has-key-development-mode' }
		}

		// Borrowed idea from AG Grid:
		// Copying from various sources (like PDFs) can include zero-width characters.
		// This helps makes sure the key validation doesn't fail.
		let cleanedLicenseKey = licenseKey.replace(/[\u200B-\u200D\uFEFF]/g, '')
		cleanedLicenseKey = cleanedLicenseKey.replace(/\r?\n|\r/g, '')

		try {
			const licenseInfo = await this.extractLicenseKey(cleanedLicenseKey)
			const expiryDate = new Date(licenseInfo.expiryDate)
			const isAnnualLicense = this.isFlagEnabled(licenseInfo.flags, FLAGS.ANNUAL_LICENSE)
			const isPerpetualLicense = this.isFlagEnabled(licenseInfo.flags, FLAGS.PERPETUAL_LICENSE)
			const isEvaluationLicense = this.isFlagEnabled(licenseInfo.flags, FLAGS.EVALUATION_LICENSE)

			const isAnnualLicenseExpired = isAnnualLicense && this.isAnnualLicenseExpired(expiryDate)
			const isPerpetualLicenseExpired =
				isPerpetualLicense && this.isPerpetualLicenseExpired(expiryDate)

			// The collaboration umbrella grants all of its sub-features, so commenting is enabled
			// by either the commenting flag or the collaboration flag.
			const isCollaborationEnabled = this.isFlagEnabled(licenseInfo.flags, FLAGS.FEAT_COLLABORATION)
			const isCommentingEnabled =
				isCollaborationEnabled || this.isFlagEnabled(licenseInfo.flags, FLAGS.FEAT_COMMENTING)

			// For perpetual licenses, the calendar expiry date only gates access to future
			// major/minor releases; it does not "expire" the license itself. While the user
			// is still on a covered version we report `daysSinceExpiry` as 0 so consumers
			// (and the grace-period warning) don't treat them as past expiry.
			const daysSinceExpiry =
				isPerpetualLicense && !isPerpetualLicenseExpired ? 0 : this.getDaysSinceExpiry(expiryDate)

			const result: ValidLicenseKeyResult = {
				license: licenseInfo,
				isLicenseParseable: true,
				isDevelopment: this.isDevelopment,
				isDomainValid: this.isDomainValid(licenseInfo),
				expiryDate,
				isAnnualLicense,
				isAnnualLicenseExpired,
				isPerpetualLicense,
				isPerpetualLicenseExpired,
				isInternalLicense: this.isFlagEnabled(licenseInfo.flags, FLAGS.INTERNAL_LICENSE),
				isNativeLicense: this.isNativeLicense(licenseInfo),
				isLicensedWithWatermark: this.isFlagEnabled(licenseInfo.flags, FLAGS.WITH_WATERMARK),
				isEvaluationLicense,
				isEvaluationLicenseExpired:
					isEvaluationLicense && this.isEvaluationLicenseExpired(expiryDate),
				isCollaborationEnabled,
				isCommentingEnabled,
				daysSinceExpiry,
			}
			this.outputLicenseInfoIfNeeded(result)

			return result
		} catch (e: any) {
			this.outputInvalidLicenseKey(e.message)
			// If the license can't be parsed, it's invalid
			return { isLicenseParseable: false, reason: 'invalid-license-key' }
		}
	}

	private isDomainValid(licenseInfo: LicenseInfo) {
		const currentHostname = this.getOriginLocation().hostname

		return licenseInfo.hosts.some((host) => {
			const normalizedHostOrUrlRegex = host.toLowerCase().trim()

			// Allow the domain if listed and www variations, 'example.com' allows 'example.com' and 'www.example.com'
			if (
				normalizedHostOrUrlRegex === currentHostname ||
				`www.${normalizedHostOrUrlRegex}` === currentHostname ||
				normalizedHostOrUrlRegex === `www.${currentHostname}`
			) {
				return true
			}

			// If host is '*', we allow all domains.
			if (host === '*') {
				// All domains allowed.
				return true
			}

			// Native license support
			// In this case, `normalizedHost` is actually a protocol, e.g. `app-bundle:`
			if (this.isNativeLicense(licenseInfo)) {
				return new RegExp(normalizedHostOrUrlRegex).test(window.location.href)
			}

			// Glob testing, we only support '*.somedomain.com' right now.
			if (host.includes('*')) {
				const globToRegex = new RegExp(
					normalizedHostOrUrlRegex.replace(/\./g, '\\.').replace(/\*/g, '.*?') + '$'
				)
				return globToRegex.test(currentHostname) || globToRegex.test(`www.${currentHostname}`)
			}

			// VSCode support
			if (window.location.protocol === 'vscode-webview:') {
				const currentUrl = new URL(window.location.href)
				const extensionId = currentUrl.searchParams.get('extensionId')
				if (normalizedHostOrUrlRegex === extensionId) {
					return true
				}
			}

			return false
		})
	}

	private isNativeLicense(licenseInfo: LicenseInfo) {
		return this.isFlagEnabled(licenseInfo.flags, FLAGS.NATIVE_LICENSE)
	}

	private getExpirationDateWithoutGracePeriod(expiryDate: Date) {
		// The named expiry date is the last day the license is fully usable, so the license
		// expires at the end of that day, i.e. the start of the following day. We work in UTC
		// (the expiry date is minted and parsed as a UTC date-only string) so the cutoff is a
		// single predictable instant for every user regardless of their local timezone.
		return new Date(
			Date.UTC(
				expiryDate.getUTCFullYear(),
				expiryDate.getUTCMonth(),
				expiryDate.getUTCDate() + 1 // Add 1 day so the named date is fully usable
			)
		)
	}

	private getExpirationDateWithGracePeriod(expiryDate: Date) {
		return new Date(
			Date.UTC(
				expiryDate.getUTCFullYear(),
				expiryDate.getUTCMonth(),
				expiryDate.getUTCDate() + GRACE_PERIOD_DAYS + 1 // Add 1 day to include the expiration day
			)
		)
	}

	private isAnnualLicenseExpired(expiryDate: Date) {
		const expiration = this.getExpirationDateWithGracePeriod(expiryDate)
		return new Date() >= expiration
	}

	private isPerpetualLicenseExpired(expiryDate: Date) {
		const expiration = this.getExpirationDateWithGracePeriod(expiryDate)
		const dates = {
			major: new Date(publishDates.major),
			minor: new Date(publishDates.minor),
		}
		// We allow patch releases, but the major and minor releases should be within the expiration date
		return dates.major >= expiration || dates.minor >= expiration
	}

	private getDaysSinceExpiry(expiryDate: Date): number {
		const now = new Date()
		const expiration = this.getExpirationDateWithoutGracePeriod(expiryDate)
		const diffTime = now.getTime() - expiration.getTime()
		const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
		return Math.max(0, diffDays)
	}

	private isEvaluationLicenseExpired(expiryDate: Date): boolean {
		// Evaluation licenses have no grace period - they expire immediately
		const now = new Date()
		const expiration = this.getExpirationDateWithoutGracePeriod(expiryDate)
		return now >= expiration
	}

	private isFlagEnabled(flags: number, flag: number) {
		return (flags & flag) === flag
	}

	private outputNoLicenseKeyProvided() {
		// Noop, we don't need to show this message.
		// this.outputMessages([
		// 	'No tldraw license key provided!',
		// 	`Please reach out to ${LICENSE_EMAIL} if you would like to license tldraw or if you'd like a trial.`,
		// ])
	}

	private outputInvalidLicenseKey(msg: string) {
		this.outputMessages(['Invalid tldraw license key', `Reason: ${msg}`])
	}

	private outputLicenseInfoIfNeeded(result: ValidLicenseKeyResult) {
		// If we added a new flag it will be twice the value of the currently highest flag.
		// And if all the current flags are on we would get the `HIGHEST_FLAG * 2 - 1`, so anything higher than that means there are new flags.
		if (result.license.flags >= HIGHEST_FLAG * 2) {
			this.outputMessages(
				[
					'Warning: This tldraw license contains some unknown flags.',
					'This will still work, however, you may want to update tldraw packages to a newer version to get access to new functionality.',
				],
				'warning'
			)
		}
	}

	private outputMessages(messages: string[], type: 'warning' | 'error' = 'error') {
		if (this.isTest) return
		if (this.verbose) {
			this.outputDelimiter(type)
			for (const message of messages) {
				const bgColor = type === 'warning' ? 'orange' : 'crimson'
				// eslint-disable-next-line no-console
				console.log(
					`%c${message}`,
					`color: white; background: ${bgColor}; padding: 2px; border-radius: 3px;`
				)
			}
			this.outputDelimiter(type)
		}
	}

	private outputDelimiter(type: 'warning' | 'error' = 'error') {
		const bgColor = type === 'warning' ? 'orange' : 'crimson'
		// eslint-disable-next-line no-console
		console.log(
			'%c-------------------------------------------------------------------',
			`color: white; background: ${bgColor}; padding: 2px; border-radius: 3px;`
		)
	}

	static className = 'tl-watermark_SEE-LICENSE'
}

/**
 * Derives which licensable features are enabled from the parse result and the derived license
 * state. In development every feature is enabled so SDK developers can build against them; in
 * production a feature requires both an active, valid license and the corresponding flag.
 *
 * @internal
 */
export function getEnabledFeatures(
	result: LicenseFromKeyResult,
	licenseState: LicenseState,
	isDevelopment: boolean
): Record<LicenseFeatureName, boolean> {
	// Development gets all features so SDK developers can build against them.
	if (isDevelopment) {
		return { ...ALL_FEATURES }
	}

	// Features require an active, valid license. Both the 30-day grace-period 'licensed' state and
	// the watermark state count as valid; unlicensed/expired/pending do not.
	if (
		!result.isLicenseParseable ||
		(licenseState !== 'licensed' && licenseState !== 'licensed-with-watermark')
	) {
		return { ...NO_FEATURES }
	}

	// Evaluation licenses get every feature so prospects can trial the whole product without us
	// having to mint per-feature evaluation keys. Expired evaluation licenses are already excluded
	// above: they resolve to the 'expired' state.
	if (result.isEvaluationLicense) {
		return { ...ALL_FEATURES }
	}

	return {
		collaboration: result.isCollaborationEnabled,
		commenting: result.isCommentingEnabled,
	}
}

export function getLicenseState(
	result: LicenseFromKeyResult,
	outputMessages: (messages: string[]) => void,
	isDevelopment: boolean
): LicenseState {
	if (!result.isLicenseParseable) {
		if (isDevelopment) {
			return 'unlicensed'
		}

		// All unlicensed scenarios should not work in production
		if (result.reason === 'no-key-provided') {
			outputMessages([
				'No tldraw license key provided!',
				'A license is required for production deployments.',
				`Please reach out to ${LICENSE_EMAIL} to purchase a license.`,
			])
		} else {
			outputMessages([
				'Invalid license key. tldraw requires a valid license for production use.',
				`Please reach out to ${LICENSE_EMAIL} to purchase a license.`,
			])
		}
		return 'unlicensed-production'
	}

	if (!result.isDomainValid && !result.isDevelopment) {
		outputMessages([
			'License key is not valid for this domain.',
			'A license is required for production deployments.',
			`Please reach out to ${LICENSE_EMAIL} to purchase a license.`,
		])
		return 'unlicensed-production'
	}

	// Handle evaluation licenses - they expire immediately with no grace period
	if (result.isEvaluationLicense) {
		if (result.isEvaluationLicenseExpired) {
			outputMessages([
				'Your tldraw evaluation license has expired!',
				`Please reach out to ${LICENSE_EMAIL} to purchase a full license.`,
			])
			return 'expired'
		} else {
			// Valid evaluation license - tracked but no watermark shown
			return 'licensed'
		}
	}

	// Handle expired regular licenses (both annual and perpetual)
	if (result.isPerpetualLicenseExpired || result.isAnnualLicenseExpired) {
		outputMessages([
			'Your tldraw license has been expired for more than 30 days!',
			`Please reach out to ${LICENSE_EMAIL} to renew your license.`,
		])
		return 'expired'
	}

	// Check if license is past expiry date but within grace period
	const daysSinceExpiry = result.daysSinceExpiry
	if (daysSinceExpiry > 0 && !result.isEvaluationLicense) {
		outputMessages([
			'Your tldraw license has expired.',
			`License expired ${daysSinceExpiry} days ago.`,
			`Please reach out to ${LICENSE_EMAIL} to renew your license.`,
		])
		// Within 30-day grace period: still licensed (no watermark)
		return 'licensed'
	}

	// License is valid, determine if it has watermark
	if (result.isLicensedWithWatermark) {
		return 'licensed-with-watermark'
	}

	return 'licensed'
}
